/**
 * Lifecycle observer chain (ADR 0005). Moved here from `@whittlelabs/jobs`: the
 * generic claim loop no longer fires lifecycle events — the Shuttle's `onJob`
 * callback does, around its strategy demux + executor run. Trust controls (spend
 * tracker, audit log) register against this chain.
 *
 * Notification semantics:
 * - Observers run sequentially in registered order, awaiting each.
 * - A thrown error on a *non-veto* event is logged and does not interrupt.
 * - On `spend.preflight`, a thrown `SpendCapExceeded` (or any error) **aborts**
 *   the dispatch; `onJob` catches it and fails the job with `refused_over_cap`.
 */

import type { DispatchedJob } from '@whittlelabs/jobs';

/** Veto-throw type for the spend tracker. Imported by name at catch sites. */
export class SpendCapExceeded extends Error {
  constructor(public readonly cap: string, public readonly used: number, public readonly limit: number) {
    super(`Spend cap "${cap}" exceeded: used ${used}, limit ${limit}`);
    this.name = 'SpendCapExceeded';
  }
}

export type LifecycleEvent =
  | {
      kind: 'job.claimed';
      jobId: string;
      attendanceId: string;
      attempt: number;
      deadline: string | null;
      claimedAt: string;
      dispatch: DispatchedJob;
    }
  | {
      kind: 'spend.preflight';
      jobId: string;
      attendanceId: string;
      estimate: { inputTokens?: number; outputTokens?: number; costUsd?: number };
    }
  | {
      kind: 'executor.dispatch.started';
      jobId: string;
      attendanceId: string;
      executor: string;
      /** The shuttle strategy demuxed for this job (ADR 0005); replaces the
       *  pre-rendered prompt the audit log used to capture. */
      strategy: string;
    }
  | {
      kind: 'executor.dispatch.completed';
      jobId: string;
      attendanceId: string;
      executor: string;
      response: {
        rawText: string;
        parsed?: Record<string, unknown>;
        usage?: {
          inputTokens?: number;
          outputTokens?: number;
          costUsd?: number;
          aiProvider?: string;
          aiModel?: string;
        };
      };
      durationMs: number;
    }
  | {
      kind: 'output.submitted';
      jobId: string;
      attendanceId: string;
      label: string;
      content: Record<string, unknown>;
    }
  | {
      kind: 'attendance.closed';
      jobId: string;
      attendanceId: string;
      status: 'completed' | 'failed' | 'abandoned';
      reason?: string;
      totalDurationMs: number;
    };

export interface LifecycleObserver {
  readonly name: string;
  notify(event: LifecycleEvent): Promise<void>;
}

/** Events on which a thrown observer error aborts the dispatch. */
const VETO_EVENTS: ReadonlySet<LifecycleEvent['kind']> = new Set(['spend.preflight']);

export class ObserverChain {
  private readonly observers: LifecycleObserver[];

  constructor(observers: LifecycleObserver[] = []) {
    this.observers = observers;
  }

  async notify(
    event: LifecycleEvent,
    onObserverError?: (err: unknown, observerName: string, event: LifecycleEvent) => void,
  ): Promise<void> {
    const isVeto = VETO_EVENTS.has(event.kind);
    for (const observer of this.observers) {
      try {
        await observer.notify(event);
      } catch (err) {
        if (isVeto) throw err;
        if (onObserverError) onObserverError(err, observer.name, event);
      }
    }
  }

  get size(): number {
    return this.observers.length;
  }
}
